import { useEffect, useEffectEvent } from 'react';
import Modal from '@/shared/ui/Modal';
import Button from '@/shared/ui/Button';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import { useWaitlistSignup } from '@/shared/utils/useWaitlistSignup';
import { track } from '@/shared/utils/track';
import { useIsMobile } from '@/modules/use-window-width';

export default function SignupGateModal(
  props: { onClose: () => void } & (
    | {
        variant: 'vote';
        vote: 'agree' | 'disagree';
        kolHandle: string;
        signalId: string;
      }
    | { variant: 'explore'; source: 'nav' | 'explore_more' | 'ask_ai' }
  ),
) {
  const { profile, login, isAuthPending } = useProfile();
  const { email, setEmail, social, setSocial, status, submit } =
    useWaitlistSignup('market_gate');
  const isMobile = useIsMobile();

  const trackShown = useEffectEvent(() => {
    track(
      'market_gate_shown',
      props.variant === 'vote' ?
        { source: 'vote', vote: props.vote, signal_id: props.signalId }
      : { source: props.source },
    );
  });
  useEffect(() => {
    trackShown();
  }, []);

  const { onClose } = props;
  useEffect(() => {
    if (profile) onClose();
  }, [profile, onClose]);

  const done = status === 'success' || status === 'duplicate';

  return (
    <Modal closeModal={props.onClose} sheet>
      <div className="flex w-129 max-w-full flex-col gap-l px-2xl pt-2xl pb-2xl max-md:w-full max-md:pb-4xl">
        <p className="text-h1 font-medium text-gray-text">
          {props.variant === 'vote' ?
            props.vote === 'agree' ?
              `Agree with @${props.kolHandle}'s call?`
            : `Disagree with @${props.kolHandle}'s call?`
          : 'Invite-only, but not forever!'}
        </p>
        {done ?
          <p className="text-body-lg font-medium text-gray">
            {status === 'duplicate' ?
              "You're already on the waitlist — we'll be in touch."
            : "You're on the waitlist! We'll be in touch via email (no spam)."}
          </p>
        : <>
            <p className="text-body-lg font-medium text-gray">
              {props.variant === 'vote' ?
                'Sign in to make your prediction.'
              : 'Alpha allocation community: traders post predictions and earn funding. Backers put SAN behind them: the locked SAN never touches trades. Profits split 50/25/25.'
              }
            </p>
            <p className="text-body-lg font-medium text-gray-text">
              {props.variant === 'vote' ?
                'No account? Join the waitlist to vote on trending crypto calls, win funding, and more.'
              : 'Want in? First adopters become Score OGs with a boosted profit share.'
              }
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              className="flex flex-col gap-m"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                disabled={status === 'loading'}
                className="h-12 w-full rounded-lg bg-background-main px-l text-body-lg font-medium text-gray-text placeholder:text-gray-2 focus:outline-none"
              />
              <textarea
                rows={2}
                value={social}
                onChange={(e) => setSocial(e.target.value)}
                placeholder={
                  isMobile ?
                    'Your X, Telegram, or trading profile, and why you would like to try Score'
                  : 'Your X, Telegram, or trading profile,\nand why you would like to try Score'
                }
                disabled={status === 'loading'}
                className="w-full resize-none rounded-lg bg-background-main px-l py-m text-body-lg font-medium text-gray-text placeholder:text-gray-2 focus:outline-none"
              />
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="h-12 w-full"
                disabled={status === 'loading' || email === ''}
                loading={status === 'loading'}
              >
                Join waitlist
              </Button>
              {status === 'error' && (
                <p className="text-body-sm text-red-default">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </>
        }
        <p className="text-center text-body-md font-medium text-gray-text">
          Have an account?{' '}
          <button
            type="button"
            className="btn-reset inline text-purple-default disabled:opacity-50"
            disabled={isAuthPending}
            onClick={() => {
              track('market_signin_clicked', { source: 'gate_modal' });
              login();
            }}
          >
            Sign in
          </button>
        </p>
      </div>
    </Modal>
  );
}
